import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import { EmployerHealthInsuranceReductionForm } from "./Form";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import { EezyCompany } from "../../../invoices/invoiceAPI";
import { ModalState } from "../../SalariesPage";
import { useEffect } from "react";
import { useAppSelector } from "../../../../app/hooks";
import { selectEmployerHealthInsuranceReductionById } from "../employerHealthInsuranceReductionsSlice";

export type FormValues = {
  id: string;
  employer: EezyCompany | null;
  employerCostCenter: {
    name: string;
    code: string;
    eezyCostCenterId: string;
  } | null;
  customerOrganization: { id: string; name: string } | null;
  yearMonth: Date | null;
  amount: string;
  identifier: string;
  version: number;
};

type Props = {
  modalState: ModalState;
  closeModal: () => void;
};

export const EditEmployerHealthInsuranceReduction = ({
  modalState,
  closeModal,
}: Props) => {
  const { t } = useTranslation();

  const defaultValues: FormValues = {
    id: "",
    employer: null,
    employerCostCenter: null,
    customerOrganization: null,
    yearMonth: null,
    amount: "",
    identifier: "",
    version: 0,
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const reduction = useAppSelector((state) =>
    selectEmployerHealthInsuranceReductionById(state, modalState.id || ""),
  );

  const close = () => {
    closeModal();
    methods.reset(defaultValues);
  };

  // When user opens item for editing
  useEffect(() => {
    if (modalState.id) {
      const defaultValues: FormValues = {
        id: reduction.id,
        employer: {
          eezyCompanyId: reduction.employerId,
          name: reduction.employerName,
          businessId: "",
          isFranchise: false,
          costCenters: [],
        },
        employerCostCenter: {
          name: reduction.employerCostCenterName,
          code: "",
          eezyCostCenterId: reduction.employerCostCenterId,
        },
        customerOrganization: {
          id: reduction.customerOrganizationId || "",
          name: reduction.customerOrganizationName || "",
        },
        yearMonth: new Date(reduction.yearMonth),
        amount: reduction.amount,
        identifier: reduction.identifier || "",
        version: reduction.version,
      };
      methods.reset(defaultValues);
    }
  }, [modalState.id, reduction, methods]);

  return (
    <DialogComponent
      open={modalState.open}
      onClose={closeModal}
      sx={{ width: "480px" }}
    >
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="header" fontWeight={700} mb="16px">
            {t(
              "payslips:newEmployerHealthInsuranceReduction",
            ).toLocaleUpperCase()}
          </Typography>
        </Box>
        <FormProvider {...methods}>
          <EmployerHealthInsuranceReductionForm onClose={close} />
        </FormProvider>
      </Box>
    </DialogComponent>
  );
};
